import React from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { DonorCombobox } from '@/components/DonorCombobox'
import { TeamPicker, useTeamMemberships, defaultTeamId } from '@/components/TeamPicker'

interface CreateOpportunitySheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface OpportunityForm {
  opportunity: {
    name: string
    estimated_amount: string
    donor_type: string
    donor_id: string
    description: string
    expected_close_date: string
  }
  // Owning-team choice (US3) — top-level sibling param, not nested.
  team_id: string
}

function defaultForm(teamId = ''): OpportunityForm {
  return {
    opportunity: {
      name: '',
      estimated_amount: '',
      donor_type: '',
      donor_id: '',
      description: '',
      expected_close_date: '',
    },
    team_id: teamId,
  }
}

export function CreateOpportunitySheet({ open, onOpenChange }: CreateOpportunitySheetProps) {
  const memberships = useTeamMemberships()
  const form = useForm<OpportunityForm>(defaultForm(defaultTeamId(memberships)))
  const [donor, setDonor] = React.useState<{ id: number; type: string; name: string } | null>(null)

  function handleClose() {
    form.reset()
    setDonor(null)
    onOpenChange(false)
  }

  function handleDonorChange(d: { id: number; type: string; name: string } | null) {
    setDonor(d)
    form.setData('opportunity', {
      ...form.data.opportunity,
      donor_type: d?.type ?? '',
      donor_id: d ? String(d.id) : '',
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post('/fundraising/opportunities', {
      onSuccess: () => {
        form.reset()
        setDonor(null)
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Nouvelle opportunite</SheetTitle>
          <SheetDescription>
            Creez une nouvelle opportunite dans le pipeline de collecte.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {/* Owning team (multi-team users only) */}
          <TeamPicker
            value={form.data.team_id}
            onChange={(val) => form.setData('team_id', val)}
            error={form.errors.team_id}
            label="Equipe"
            placeholder="Choisir une equipe"
          />

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="opp_name">Nom *</Label>
            <Input
              id="opp_name"
              value={form.data.opportunity.name}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  name: e.target.value,
                })
              }
              required
              placeholder="Nom de l'opportunite"
              autoFocus
            />
            {form.errors['opportunity.name'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity.name']}</p>
            )}
          </div>

          {/* Donor */}
          <div className="space-y-2">
            <Label>Donateur *</Label>
            <DonorCombobox
              value={donor}
              onChange={handleDonorChange}
              error={form.errors['opportunity.donor'] || form.errors['opportunity.donor_id']}
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="opp_amount">Montant estime (TND)</Label>
            <Input
              id="opp_amount"
              type="number"
              min="0"
              step="1"
              value={form.data.opportunity.estimated_amount}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  estimated_amount: e.target.value,
                })
              }
              placeholder="0"
            />
            {form.errors['opportunity.estimated_amount'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity.estimated_amount']}</p>
            )}
          </div>

          {/* Expected close date */}
          <div className="space-y-2">
            <Label htmlFor="opp_close_date">Date de cloture prevue</Label>
            <Input
              id="opp_close_date"
              type="date"
              value={form.data.opportunity.expected_close_date}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  expected_close_date: e.target.value,
                })
              }
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="opp_description">Notes</Label>
            <Textarea
              id="opp_description"
              value={form.data.opportunity.description}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  description: e.target.value,
                })
              }
              placeholder="Notes internes sur cette opportunite..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing} className="flex-1">
              {form.processing ? 'Creation...' : 'Creer'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
